import { at, object, text } from './repository';
import type { Action, Field, Row, Value } from './types';

export function visible(field: Field, values: Row): boolean {
  return !field.when || field.when.values.includes(values[field.when.field] ?? null);
}
export function initialValues(fields: Field[], data: Row): Row {
  return Object.fromEntries(
    fields.map((field) => [
      field.name,
      (field.source ? at(data, field.source) : undefined) ??
        field.initial ??
        (field.kind === 'rows' ? [] : field.kind === 'boolean' ? false : ''),
    ])
  );
}
export function formPayload(fields: Field[], values: Row, context: Row = {}): Row {
  const result: Row = {};
  for (const field of fields) {
    if (!visible(field, { ...context, ...values })) continue;
    const value = values[field.name];
    const validation = field.validate?.(text(value));
    if (validation) throw new Error(`${field.label}: ${validation}`);
    if (
      (typeof value === 'string' && value.trim() === '') ||
      value === undefined ||
      value === null
    ) {
      if (field.required) throw new Error(`${field.label} is required.`);
      continue;
    }
    if (field.omit) continue;
    if (field.kind === 'rows') {
      if (!Array.isArray(value) || (field.required && value.length === 0))
        throw new Error(`Add at least one ${field.label.toLowerCase()} row.`);
      result[field.name] = value.map((row) =>
        formPayload(field.fields ?? [], object(row), { ...context, ...values })
      );
    } else if (field.kind === 'mapping') {
      const mapping: Row = {};
      for (const row of Array.isArray(value) ? value : []) {
        const entry = object(row);
        if (!text(entry.sheet) || !text(entry.room))
          throw new Error('Choose a sheet name and storage room for each mapping.');
        if (mapping[text(entry.sheet)]) throw new Error('A sheet may only be mapped once.');
        mapping[text(entry.sheet)] = entry.room!;
      }
      result[field.name] = mapping;
    } else if (field.kind === 'number') {
      const number = Number(value);
      if (
        !Number.isFinite(number) ||
        (field.min !== undefined && number < field.min) ||
        (field.max !== undefined && number > field.max) ||
        (field.step === '1' && !Number.isInteger(number))
      )
        throw new Error(`${field.label} is outside the permitted range.`);
      result[field.name] = number;
    } else if (field.kind === 'decimal') {
      if (!/^-?\d+(\.\d{1,4})?$/.test(text(value)))
        throw new Error(`${field.label} must be a decimal with at most four places.`);
      result[field.name] = text(value);
    } else if (field.kind === 'multi') {
      if (!Array.isArray(value) || (field.required && !value.length))
        throw new Error(`Select ${field.label.toLowerCase()}.`);
      result[field.name] = value;
    } else if (field.kind === 'datetime') {
      if (
        !/^\d{4}-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:\d{2})$/.test(text(value)) ||
        !Number.isFinite(Date.parse(text(value)))
      )
        throw new Error(
          `${field.label} needs a date, time and UTC offset, for example 2026-09-01T00:00:00+09:00.`
        );
      result[field.name] = value;
    } else result[field.name] = typeof value === 'string' ? value.trim() : value;
  }
  return result;
}
export function available(action: Action, data: Row): boolean {
  return (
    (!action.allowed || action.allowed(data)) &&
    (!action.requiresRecord || Boolean(text(data.id))) &&
    (!action.statuses ||
      action.statuses.includes(text(at(data, action.statusField ?? 'status')))) &&
    (!action.when || action.when.values.includes(at(data, action.when.field) ?? null))
  );
}
export function label(key: string): string {
  const names: Record<string, string> = {
    id: 'Record reference',
    gi_date: 'Goods-issue date',
    base_quantity: 'Quantity in base unit',
    reported_quantity: 'Reported quantity',
    position_of_record: 'Application stock',
    reason_code: 'Reason',
    max_c: 'Maximum (°C)',
    min_c: 'Minimum (°C)',
    expected_revision: 'Revision',
    customer_remaining_qty: 'Customer remaining quantity',
    in_progress: 'Awaiting issuance',
    still_to_invoice: 'Still to invoice',
  };
  return (
    names[key] ??
    key
      .replace(/_id$/, ' reference')
      .replaceAll('_', ' ')
      .replace(/^./, (c) => c.toUpperCase())
  );
}
export function scalar(value: Value | undefined): string {
  if (value === null || value === undefined || value === '') return 'Not recorded';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return text(value);
}
